import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DistributionAccount} from '../../../../distribution-account/distribution-account.model';
import {HttpClientWithCache} from '../../../../utils/http-client-with-cache';
import {environment} from '../../../../../environments/environment';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {SubscriptionHandler} from '../../../../utils/subscription-handler';
import {GoogleDdpPriceDialogComponent} from './google-ddp-price-dialog.component';
import {License, LicenseStatus} from './domain';
import {TargetService} from '../../../../target/target.service';
import {GoogleShareConfig} from '../../../../segment-multi-share/segment-share.service';
import {ErrorModel} from '../../../../app.model';
import {HttpInteraction} from '../../../../utils/http-interactions.utils';

@Component({
  selector: 'app-segment-info-google-ddp',
  template: `
    <ng-container *ngIf="{
                licensees: licensee | async,
                publicLicensee: publicLicensee | async,
                partnerLicensees: partnerLicensee | async,
                advertiserLicensees: advertiserLicensee | async,
                adwordsLicensees: adwordsLicensee | async
              } as ctx">


      <div *ngIf="ctx.licensees !== undefined && ctx.licensees.length === 0 && error === undefined">none</div>

      <mat-spinner *ngIf="ctx.licensees === undefined && error === undefined" [diameter]="32"></mat-spinner>


      <div class="error" *ngIf="error !== undefined">{{error}}</div>

      <ng-container *ngIf="ctx.publicLicensee !== undefined">
        <app-google-ddp-prices title="Public Price" [licensee]="[ctx.publicLicensee]" (priceEdited)="editPrice($event)"
                               [removable]="removableLicences"
                               (priceRemoved)="removePrice($event)"></app-google-ddp-prices>
      </ng-container>

      <ng-container *ngIf="ctx.partnerLicensees !== undefined && ctx.partnerLicensees.length">
        <app-google-ddp-prices title="Partner Price" [licensee]="ctx.partnerLicensees"
                               (priceEdited)="editPrice($event)"
                               [removable]="removableLicences"
                               (priceRemoved)="removePrice($event)"></app-google-ddp-prices>
      </ng-container>

      <ng-container *ngIf="ctx.advertiserLicensees !== undefined && ctx.advertiserLicensees.length > 0">
        <app-google-ddp-prices title="Advertiser Price" [licensee]="ctx.advertiserLicensees"
                               (priceEdited)="editPrice($event)" [removable]="removableLicences"
                               (priceRemoved)="removePrice($event)"></app-google-ddp-prices>
      </ng-container>

      <ng-container *ngIf="ctx.adwordsLicensees !== undefined && ctx.adwordsLicensees.length > 0">
        <app-google-ddp-prices title="Adwords Price" [licensee]="ctx.adwordsLicensees"
                               (priceEdited)="editPrice($event)"
                               [removable]="removableLicences"
                               (priceRemoved)="removePrice($event)"></app-google-ddp-prices>
      </ng-container>

      <mat-card>
        <mat-card-content>
          <div class="add-seat-price-container" style="top:20px">
            <button class="add-button" mat-raised-button matTooltip="Add licensee" color="accent" name="add-licensee"
                    (click)="this.addPrice()">
              <mat-icon>add</mat-icon>
              Add Licensee
            </button>
          </div>
        </mat-card-content>
      </mat-card>

    </ng-container>
  `,
  styleUrls: ['../account/account.component.scss', 'account-google-ddp.component.scss']
})
export class SegmentInfoGoogleDdpComponent implements OnInit, OnDestroy {

  @Input() account!: DistributionAccount;
  @Output() priceChange = new EventEmitter<GoogleShareConfig>();
  isDryMode = false;
  error: string | undefined;
  removableLicences = false;
  licenseSource: BehaviorSubject<License[]> = new BehaviorSubject([] as License[]);
  licensee = this.licenseSource.asObservable();
  publicLicensee = this.licensee.pipe(
    map(all => all?.find(licensee => licensee.clientProduct === 'RESELLER'))
  );
  partnerLicensee = this.licensee.pipe(
    map(all => all?.filter(licensee => licensee.clientProduct === 'DISPLAY_VIDEO_PARTNER'))
  );
  advertiserLicensee = this.licensee.pipe(
    map(all => all?.filter(licensee => licensee.clientProduct === 'DISPLAY_VIDEO_ADVERTISER'))
  );
  adwordsLicensee = this.licensee.pipe(
    map(all => all?.filter(licensee => licensee.clientProduct === 'GOOGLE_ADS'))
  );
  private subscriptionHandler = new SubscriptionHandler();

  constructor(private httpClient: HttpClientWithCache, private dialog: MatDialog, private targetService: TargetService) {
  }

  ngOnInit(): void {
    this.isDryMode = !this.account?.foreignIdForCurrentSegment;

    if (this.isDryMode) {
      this.removableLicences = true;
      this.licenseSource.next([]);
    } else {
      this.fetchData();
    }

    this.licensee.subscribe((val) => {
      this.priceChange.emit({
        licensees: val
      } as GoogleShareConfig);
    });
  }

  ngOnDestroy() {
    this.subscriptionHandler.unsubscribe();
  }

  addPrice() {
    const dialogRef = this.dialog.open(GoogleDdpPriceDialogComponent);
    this.subscriptionHandler.subscription = dialogRef.afterClosed().subscribe((licensee?: License) => {
      if (!licensee) {
        return;
      }
      const licenseWithUserListId = {
        ...licensee,
        userListId: Number(this.account?.foreignIdForCurrentSegment)
      } as License;
      if (this.isDryMode) {
        this.addPriceLocally(licenseWithUserListId);
      } else {
        this.targetService.addGoogleDdpPrice(licenseWithUserListId).subscribe({
          next: (res) => this.addPriceLocally(res),
          error: (err) => this.handleResponseError(HttpInteraction.errorModel(err))
        });
      }
    });
  }

  editPrice(licensee: License) {
    const initEdit = () => {
      const dialogRef = this.dialog.open(GoogleDdpPriceDialogComponent, {data: {...licensee}});
      this.subscriptionHandler.subscription = dialogRef.afterClosed().subscribe((newLicense: License) => {
        if (!newLicense) {
          return;
        }
        if (this.isDryMode) {
          this.editPriceLocally(newLicense);
        } else {
          this.targetService.updateGoogleDdpPrice(newLicense).subscribe({
            next: (res) => this.editPriceLocally(res),
            error: (err) => this.handleResponseError(HttpInteraction.errorModel(err))
          });
        }
      });
    };

    if (licensee.status === LicenseStatus.DISABLED) {
      const confirmRef = this.dialog.open(GoogleDdpPriceActivationDialogComponent);
      confirmRef.afterClosed().subscribe((proceed) => {
        if (proceed) {
          initEdit();
        }
      });
    } else {
      initEdit();
    }
  }

  removePrice(licensee: License) {
    if (!this.isDryMode) {
      return;
    }
    const newEntries = [...this.licenseSource.getValue()];
    const idx = newEntries.findIndex((val) => val.clientId === licensee.clientId);
    newEntries.splice(idx, 1);
    this.licenseSource.next(newEntries);
  }

  handleResponseError(err: ErrorModel) {
    this.error = `${err.message}`;
  }

  private fetchData() {
    if (this.isDryMode) {
      return;
    }

    const url = `${environment.apiSegments}/google/ddp/licensees?userListId=${this.account?.foreignIdForCurrentSegment}`;
    this.subscriptionHandler.subscription =
      this.httpClient.get<License[]>(url)
        .subscribe({
          next: res => {
            this.licenseSource.next(res);
          },
          error: (error) => {
            this.error = `Error: Could not fetch prices. (${HttpInteraction.errorModel(error).message})`;
          }
        });
  }

  private addPriceLocally(result: License) {
    this.licenseSource.next([...this.licenseSource.getValue(), result]);
  }

  private editPriceLocally(result: License) {
    const newEntries = [...this.licenseSource.getValue()];
    const idx = newEntries.findIndex((val) => val.clientId === result.clientId);
    newEntries.splice(idx, 1, result);
    this.licenseSource.next(newEntries);
  }
}

@Component({
  selector: 'app-account-google-ddp-price-activation-dialog',
  templateUrl: 'account-google-ddp-price-activation-dialog.html',
})
export class GoogleDdpPriceActivationDialogComponent {
}
